import * as Sentry from '@sentry/react';
import type { User } from '@supabase/supabase-js';

/**
 * Initialize Sentry error tracking
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn || dsn === 'your-sentry-dsn-here') {
    if (import.meta.env.DEV) {
      console.warn('Sentry DSN not configured. Error tracking disabled.');
    }
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    enabled: import.meta.env.PROD,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

/**
 * Set user context for Sentry error tracking
 */
export function setSentryUser(user: User | null): void {
  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.user_metadata?.name,
    });
  } else {
    Sentry.setUser(null);
  }
}

/**
 * Clear user context from Sentry
 */
export function clearSentryUser(): void {
  Sentry.setUser(null);
}

interface CaptureErrorOptions {
  /** Additional context tags */
  tags?: Record<string, string>;
  /** Extra data to attach to the error */
  extra?: Record<string, unknown>;
  /** Error severity level */
  level?: 'fatal' | 'error' | 'warning' | 'info' | 'debug';
  /** Fingerprint for grouping errors */
  fingerprint?: string[];
}

/**
 * Manually capture an error with additional context
 */
export function captureError(
  error: Error | string,
  options: CaptureErrorOptions = {}
): string {
  const { tags, extra, level = 'error', fingerprint } = options;

  const errorObj = typeof error === 'string' ? new Error(error) : error;

  return Sentry.captureException(errorObj, {
    tags,
    extra,
    level,
    fingerprint,
  });
}

/**
 * Capture a message with context
 */
export function captureMessage(
  message: string,
  options: CaptureErrorOptions = {}
): string {
  const { tags, extra, level = 'info', fingerprint } = options;

  return Sentry.captureMessage(message, {
    tags,
    extra,
    level,
    fingerprint,
  });
}

/**
 * Add breadcrumb for debugging context
 */
export function addBreadcrumb(
  category: string,
  message: string,
  data?: Record<string, unknown>
): void {
  Sentry.addBreadcrumb({
    category,
    message,
    data,
    level: 'info',
  });
}

/**
 * Set additional context tags
 */
export function setTags(tags: Record<string, string>): void {
  Sentry.setTags(tags);
}

/**
 * Set extra context data
 */
export function setExtra(key: string, value: unknown): void {
  Sentry.setExtra(key, value);
}

export { Sentry };
